import { AnyObject, Async, EmptyObject, F1 } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const _noop = () => ({} as any);

export const factory =
  <T extends AnyObject>(defaults: T) =>
  (params: Partial<T> = {}) =>
    ({ ...defaults, ...(params as any) } as T);

export const split = <T>(vs: Array<T>, cond: (v: T) => boolean): [T[], T[]] =>
  vs.reduce(([ls, rs], v) => (cond(v) ? [[...ls, v], rs] : [ls, [...rs, v]]), [
    [],
    [],
  ] as [T[], T[]]);

export const keys = <T>(t: T): Array<keyof T> =>
  t && typeof t === 'object' ? (Object.keys(t) as any) : [];

export const values = <T>(t: T) => keys(t || ({} as any as T)).map((k) => t[k]);
export const arrify = <T>(ts: T[] | T): T[] => (Array.isArray(ts) ? ts : [ts]);

export const notNull = <T>(v: T | null) => v !== null;
export const filterNulls = <T>(vs: (T | null)[]) => vs.filter(notNull) as T[];
export const pickObject = <T, K extends keyof T>(
  source: T,
  keysToPick: readonly K[],
): Pick<T, K> => {
  const res: Pick<T, K> = {} as any;
  keysToPick.forEach((k) => (res[k] = source[k]));
  return res;
};

export const omitObject = <T, K extends keyof T>(
  source: T,
  keysToOmit: readonly K[],
): Omit<T, K> => {
  const res: T = {} as any;
  (keys(source) as K[])
    .filter((k) => !keysToOmit.includes(k))
    .forEach((k) => (res[k] = source[k]));
  return res;
};

export const isUnionType =
  <T extends string>(vs: Array<T>) =>
  (v: any): v is T =>
    vs.includes(v);

export const getFirstWord = (text: string) => text.split(' ')[0];
export const getWithoutFirstWord = (text: string) =>
  text.split(' ').slice(1).join(' ');

export const asyncAction = <TRes>(
  setState: F1<Async<TRes>>,
  callback: () => Promise<TRes>,
) => {
  setState({ type: 'fetching' });
  callback()
    .then((value) => setState({ type: 'fetched', value }))
    .catch((err) => setState({ type: 'fetch-error', value: err }));
  return null;
};

export const toArray = <T>(cnt: number, cb: (i: number) => T): Array<T> =>
  Array(cnt)
    .fill(0)
    .map((_, i) => cb(i));

const sleep = (t: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, t));

export const poll = async <T>(
  asyncFn: () => Promise<T | null>,
  options?: {
    timeout?: number;
    retry?: number;
  },
) => {
  let maxTries = options?.retry ?? 100;

  while (maxTries > 0) {
    const data = await asyncFn();

    if (data !== null) return data;

    await sleep(options?.timeout ?? 1000);
    maxTries -= 1;
  }
};

export const pluralize = (word: string, count: number) => {
  return count === 1 ? word : `${word}s`;
};
export type NamedEntry<T extends Record<string, unknown>> = {
  name: keyof T & string;
  value: T[keyof T];
};

export type NamedEntries<T extends Record<string, unknown>> = Array<
  NamedEntry<T>
>;

export const toNamedEntries = <T extends EmptyObject>(
  obj: T,
): NamedEntries<T> =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined)
    .map(([name, value]) => ({
      name: name as keyof T & string,
      value: value as T[keyof T],
    }));
